/**
 * React Query hook for listing all documents for a story
 * Story 297: Agent research docs visible in UI as dynamic tabs
 *
 * Returns the list of documents (prd, design, and any generic docs)
 * with display_name and section_count for tab discovery.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/** A single document entry returned by the list endpoint */
export interface DocumentListItem {
  doc_type: string;
  display_name: string | null;
  section_count: number;
}

/** Response from GET /api/v1/projects/:slug/stories/:id/documents */
interface DocumentsListResponse {
  documents: DocumentListItem[];
}

/** Payload for POST /api/v1/projects/:slug/stories/:id/documents */
interface CreateDocumentPayload {
  doc_type: string;
  display_name: string;
}

/** Response from POST /api/v1/projects/:slug/stories/:id/documents */
interface CreateDocumentResponse {
  doc_type: string;
  display_name: string;
  id: string;
}

/** Query key factory for story documents list */
export const storyDocumentsKeys = {
  all: ['story-documents-list'] as const,
  list: (projectSlug: string, storyId: string) =>
    [...storyDocumentsKeys.all, projectSlug, storyId] as const,
};

/** Fetch all documents for a story */
async function fetchStoryDocuments(
  projectSlug: string,
  storyId: string
): Promise<DocumentsListResponse> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch all documents for a story.
 * Used for dynamic tab discovery in StoryDetailPanel.
 *
 * @param projectSlug - Project identifier (e.g., "boxabirds/claude-backlog")
 * @param storyId - Story external ID
 */
export function useStoryDocuments(
  projectSlug: string,
  storyId: string | null
) {
  return useQuery({
    queryKey: storyDocumentsKeys.list(projectSlug, storyId ?? ''),
    queryFn: () => fetchStoryDocuments(projectSlug, storyId!),
    enabled: !!projectSlug && !!storyId,
  });
}

/**
 * Mutation hook to create a new empty document (for + tab button).
 *
 * @param projectSlug - Project identifier
 * @param storyId - Story external ID
 */
export function useCreateDocument(
  projectSlug: string,
  storyId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDocumentPayload): Promise<CreateDocumentResponse> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents`;
      return api.fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      // Invalidate the documents list so the new tab appears
      queryClient.invalidateQueries({
        queryKey: storyDocumentsKeys.list(projectSlug, storyId),
      });
    },
  });
}
